// src/pages/bank/BankDialog.tsx

import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Save, Plus, X, Landmark, DollarSign, CreditCard, Code } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { ETabs } from "../../../components/ETabs";
import { Dropdown } from "primereact/dropdown";
import { Bank, BankPayload } from "../../../types/bank";


// BankPayload tipini doğrudan kullanabiliriz
type BankFormState = BankPayload;

interface BankDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bank: Bank | null;
  onSave: (data: BankPayload) => void; 
}

// Para Birimi Seçenekleri (Kasa ile aynı)
const CURRENCY_OPTIONS = [
    { label: 'Türk Lirası (TRY)', value: 'TRY' },
    { label: 'ABD Doları (USD)', value: 'USD' },
    { label: 'Euro (EUR)', value: 'EUR' },
];

const initialData: BankFormState = {
  name: "",
  iban: null,
  branch: null,
  currency: "TRY",
};


export const BankDialog: React.FC<BankDialogProps> = ({
  isOpen,
  onClose,
  bank,
  onSave,
}) => {
  const [formData, setFormData] = useState<BankFormState>(initialData);
  const [activeIndex, setActiveIndex] = useState(0); 

   
useEffect(() => {
  if (!isOpen) return;

  if (bank) {
    setFormData({
      name: bank.name || "",
      iban: bank.iban || null,
      branch: bank.branch || null,
      currency: bank.currency || "TRY",
    });
  } else {
    setFormData(initialData);
  }

  setActiveIndex(0); 

}, [isOpen, bank]);

  // FORM HANDLER
  const handleChange = (field: keyof BankFormState, value: string | undefined | null) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };
  
const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) { 
      setActiveIndex(0); 
      console.error("Banka Adı zorunludur.");
      return;
    }
    
    // Payload'a balance eklemiyoruz.
    const payload: BankPayload = {
        name: formData.name,
        iban: formData.iban,
        branch: formData.branch,
        currency: formData.currency || "TRY",
    };

    onSave(payload);
  };

  const tabs = useMemo(() => [
    {
      id: 'basic-info',
      header: <><Landmark className="w-4 h-4 mr-2" /> Hesap Bilgileri</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* 1. Banka Adı */}
            <div className="flex flex-col gap-2">
              <label htmlFor="name">Banka Adı *</label>
              <InputText
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Örn: Garanti Bankası"
              />
            </div>

            {/* 2. Para Birimi */}
            <div className="flex flex-col gap-2">
              <label htmlFor="currency">Para Birimi *</label>
              <Dropdown
                id="currency"
                value={formData.currency}
                options={CURRENCY_OPTIONS}
                onChange={(e) => handleChange('currency', e.value)}
                placeholder="Para Birimi Seçiniz"
              />
            </div>

            {/* 3. IBAN */}
            <div className="flex flex-col gap-2">
              <label htmlFor="iban" className="flex items-center gap-1"><CreditCard className="w-4 h-4 text-gray-500"/> IBAN</label>
              <InputText
                id="iban"
                value={formData.iban ?? ''}
                onChange={(e) => handleChange('iban', e.target.value)}
                placeholder="TRXX XXXX XXXX ..."
              />
            </div>
            
            {/* 4. Şube Adı */}
            <div className="flex flex-col gap-2">
              <label htmlFor="branch" className="flex items-center gap-1"><Code className="w-4 h-4 text-gray-500"/> Şube Adı/Kodu</label>
              <InputText
                id="branch"
                value={formData.branch ?? ''}
                onChange={(e) => handleChange('branch', e.target.value)}
                placeholder="Şube Adı veya Kodu"
              />
            </div>

          </div>
          
          {/* 5. Bakiye Bilgisi (Sadece Görüntüleme) */}
          {bank && (
              <div className="flex items-center gap-2 p-3 card font-semibold bg-blue-50 text-blue-800">
                  <DollarSign className="w-5 h-5"/>
                  <span>
                      Mevcut Bakiye: 
                      <span className="ml-2 font-bold text-lg">
                          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: bank.currency }).format(bank.balance)}
                      </span>
                  </span>
              </div>
          )}
        </div>
      )
    },
  ], [formData, bank]); 


  return (
    <Dialog
      header={bank ? "Banka Hesabı Düzenle" : "Yeni Banka Hesabı Ekle"}
      visible={isOpen}
      onHide={onClose}
      modal
        maximizable
      resizable={false}
      className="w-full lg:w-6/12 xl:w-4/12 min-h-[350px]" 
      footer={
        <div className="flex justify-end gap-3">
          <IconTextButton
            onClick={onClose}
            icon={X}
            className="btn-cancel"
          >
            İptal
          </IconTextButton>

          <IconTextButton
            onClick={handleSubmit}
            icon={bank ? Save : Plus}
            className="btn-new"
          >
            {bank ? "Güncelle" : "Hesap Ekle"}
          </IconTextButton>
        </div>
      }
    >
      <ETabs 
        tabs={tabs} 
        initialActiveIndex={activeIndex}
        onTabChange={setActiveIndex}
      />

    </Dialog>
  );
};